import { useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DownloadRounded from '@mui/icons-material/DownloadRounded';
import { useMutation } from '@tanstack/react-query';
import { api } from '@/api';
import { ApiError } from '@/api/client';
import { errorMessage } from '@/api/errorMessage';
import { useAuth } from '@/auth';
import { useNotify } from '@/components/feedback/notificationsContext';
import { SectionCard } from '@/components/SectionCard';
import { useMe } from '@/session/useSession';

const LAST_ADMIN =
  'Você é a única pessoa administradora de um espaço. Passe a administração para outra pessoa antes.';

function reason(error: unknown) {
  if (error instanceof ApiError && error.code === 'last-admin') return LAST_ADMIN;
  if (error instanceof ApiError && error.code === 'platform-admin')
    return 'Administradores da plataforma não podem excluir a própria conta por aqui.';
  return errorMessage(error);
}

/** LGPD self-service: download, leave the space erasing data, delete the account. */
export function PrivacyCard() {
  const me = useMe();
  const slug = me.tenant?.slug ?? '';
  const spaceName = me.tenant?.displayName ?? 'este espaço';
  const notify = useNotify();
  const [dialog, setDialog] = useState<'leave' | 'delete' | null>(null);

  const download = useMutation({
    mutationFn: async () => {
      const blob = await api.request<Blob>('GET', '/me/privacy/export', { parse: 'blob' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `meus-dados-${slug}.json`;
      link.click();
      URL.revokeObjectURL(url);
    },
    onSuccess: () => notify('Arquivo com seus dados baixado.', 'success'),
    onError: (error) => notify(errorMessage(error, 'Não foi possível gerar o arquivo.'), 'error'),
  });

  return (
    <SectionCard
      title="Seus dados"
      description="Seus direitos pela LGPD, sem precisar pedir para ninguém."
    >
      <Stack spacing={2.5}>
        <Stack spacing={1}>
          <Typography variant="subtitle2">Baixar uma cópia</Typography>
          <Typography variant="body2" color="text.secondary">
            Um arquivo JSON com tudo o que você tem em {spaceName}: cadastro, registros e
            consentimentos. Fotos e documentos já estão no seu Google Drive.
          </Typography>
          <Button
            variant="outlined"
            startIcon={<DownloadRounded />}
            disabled={download.isPending}
            onClick={() => download.mutate()}
            sx={{ alignSelf: 'flex-start' }}
          >
            {download.isPending ? 'Preparando…' : 'Baixar meus dados'}
          </Button>
        </Stack>
        <Stack spacing={1}>
          <Typography variant="subtitle2">Sair deste espaço</Typography>
          <Typography variant="body2" color="text.secondary">
            Apaga seus dados em {spaceName} e encerra seu acesso a ele. Suas outras contas e espaços
            continuam.
          </Typography>
          <Button
            color="error"
            variant="outlined"
            onClick={() => setDialog('leave')}
            sx={{ alignSelf: 'flex-start' }}
          >
            Sair e apagar meus dados daqui
          </Button>
        </Stack>
        <Stack spacing={1}>
          <Typography variant="subtitle2">Excluir minha conta</Typography>
          <Typography variant="body2" color="text.secondary">
            Apaga sua conta e seus dados em todos os espaços e revoga o acesso ao seu Google Drive.
            Não dá para desfazer.
          </Typography>
          <Button
            color="error"
            onClick={() => setDialog('delete')}
            sx={{ alignSelf: 'flex-start' }}
          >
            Excluir minha conta
          </Button>
        </Stack>
      </Stack>
      {dialog === 'leave' && (
        <DangerDialog
          title={`Sair de ${spaceName}?`}
          description={`Seus registros neste espaço serão apagados, os compartilhamentos com profissionais encerrados e você sairá da conta. Para confirmar, digite o endereço do espaço: ${slug}`}
          expected={slug}
          label="Endereço do espaço"
          action="Sair e apagar"
          request={(confirm) => api.post('/me/privacy/leave-space', { confirm })}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === 'delete' && (
        <DangerDialog
          title="Excluir sua conta?"
          description={`Tudo será apagado em todos os espaços. Os arquivos no seu Google Drive continuam lá, mas este app perde o acesso. Para confirmar, digite seu e-mail: ${me.user.email}`}
          expected={me.user.email}
          label="Seu e-mail"
          action="Excluir definitivamente"
          request={(confirmEmail) => api.post('/me/privacy/delete-account', { confirmEmail })}
          onClose={() => setDialog(null)}
        />
      )}
    </SectionCard>
  );
}

function DangerDialog({
  title,
  description,
  expected,
  label,
  action,
  request,
  onClose,
}: {
  title: string;
  description: string;
  expected: string;
  label: string;
  action: string;
  request(value: string): Promise<unknown>;
  onClose(): void;
}) {
  const { signOut } = useAuth();
  const [value, setValue] = useState('');
  const run = useMutation({
    mutationFn: () => request(value.trim()),
    // The session no longer exists: finish signing out locally.
    onSuccess: () => signOut(),
  });
  const matches = value.trim().toLowerCase() === expected.toLowerCase();
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <DialogContentText>{description}</DialogContentText>
          <TextField
            label={label}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            autoComplete="off"
            autoFocus
          />
          {run.isError && <Alert severity="error">{reason(run.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button
          color="error"
          variant="contained"
          disabled={!matches || run.isPending}
          onClick={() => run.mutate()}
        >
          {action}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
